import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { formatPrice, formatPeriod } from '@/utils/formatters'
import { Modal, Pagination, Spinner } from '@/components/ui'
import { useUrlFilters } from '@/hooks/useUrlFilters'
import { formatBienLabel } from '../biens/biensSlice'
import { fetchRents, setFilters, selectNotPaidQuittances } from "../quittances/quittancesSlice"
import GenerateForm from "../quittances/GenerateForm"
import SingleAppelForm from '../quittances/SingleAppelForm'

export default ({late = false}) => {
  const dispatch = useDispatch()
  const [selectedQuittanceIds, setSelectedQuittanceIds] = useState([])
  const [selectedForm, setSelectedForm] = useState("")
  
  // Récupérer les quittances depuis le store Redux
  const quittances = useSelector(state => selectNotPaidQuittances(state, late))
  const loading = useSelector(state => state.quittances.loadingStatus.fetchList)
  const filters = useSelector(state => state.quittances.filters)
  const pagination = useSelector(state => state.quittances.pagination)
  
  const fetchItems = (params) => fetchRents({
    ...params,
    ...(late ? {late: true} : {})
  })

  useUrlFilters({
    setFiltersAction: setFilters,
    fetchAction: fetchItems,
    filters,
    allowedParams: ['page', 'limit'],
    updateUrlParams: false
  })

  const handlePageChange = (page) => {
    dispatch(setFilters({page}))
  } 
 
  const { isOpen, handleShow, handleHide, dialogRef } = Modal.useModal()

  const handleQuittanceForm = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setSelectedForm("generate")
    handleShow()
  }

  const handleSingleAppelForm = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setSelectedForm("appel")
    handleShow()
  }

  const handleSuccessSubmit = () => {
    setSelectedQuittanceIds([])
    handleHide()
    dispatch(fetchItems())
  }
  
  const handleQuittanceSelection = (quittanceId) => {
    setSelectedQuittanceIds(prevIds => {
      if (prevIds.includes(quittanceId)) {
        return prevIds.filter(id => id !== quittanceId)
      } else {
        return [...prevIds, quittanceId]
      }
    })
  }

  return (
    <>
    <div className="flex justify-between items-center mb-4">
      <h2 className="text-2xl font-bold">{late ? "Loyers impayés" : "Loyers et quittances" }</h2>
      <div className="space-x-2">
        <button disabled={selectedQuittanceIds.length === 0} 
          onClick={handleQuittanceForm} 
          className="btn btn-sm btn-primary">
            Générer les quittances
          </button>
        <button disabled={selectedQuittanceIds.length !== 1}
          onClick={handleSingleAppelForm}
          className="btn btn-sm btn-primary">
            Ajouter un appel
          </button>
      </div>
    </div>
     
    {/* Liste des quittances */}
    <div className="h-full space-y-2 rounded-none p-2 bg-gray-200">
      {/* Afficher un message de chargement */}
      {loading && 
        <div className="flex justify-center p-4">
          <Spinner color='primary'/>
        </div>
      }
      {!loading && (<>
        { quittances.length === 0 ? 
          <div className="text-center p-4">{late ? "Aucun impayé" : "Aucune quittance disponible"}</div> :
          quittances.map(quittance => (
            <div
              key={quittance.id}
              className={`flex items-start p-2 rounded cursor-pointer ${selectedQuittanceIds.includes(quittance.id) ? 'bg-primary-300' : ''}`}
              onClick={() => handleQuittanceSelection(quittance.id)}
            >
              <input
                type="checkbox"
                className="checkbox mt-1 mr-2"
                checked={selectedQuittanceIds.includes(quittance.id)}
                readOnly
              />
              <div className="tooltip tooltip-right">
                <div className="tooltip-content bg-primary">
                  <div className="text-white">{formatBienLabel(quittance.bien)}</div>
                </div>
                {quittance.locataires && quittance.locataires.length > 0
                  ? quittance.locataires.map(l => l.nom_complet).join(', ')
                  : 'Locataire inconnu'} - {formatPrice(quittance.montant)} - {formatPeriod(quittance.year, quittance.month)}
              </div>
            </div>
          ))
        }
        { pagination.totalPages > 1 && (
          <Pagination 
            currentPage={pagination.currentPage}
            totalPages={pagination.totalPages}
            onPageChange={handlePageChange}
          />
        )}
      </>)}
    </div>
    <Modal
        open={isOpen}
        onClose={handleHide}
        ref={dialogRef}
        backdrop={true}
        className="max-w-full rounded-none"
      >
        <Modal.Header showCloseButton={true} />
        <Modal.Body>
          { selectedForm === "generate" ?
            <GenerateForm ids={selectedQuittanceIds} 
                onClose={handleHide} 
                onSuccess={handleSuccessSubmit} 
            /> :
            <SingleAppelForm id={selectedQuittanceIds[0]}
              onClose={handleHide}
              onSuccess={handleSuccessSubmit}
            />
          }
        </Modal.Body>
      </Modal>
    </>
  )
}